import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Badge } from '../components/ui/badge'
import { DollarSign, TrendingUp, Calendar } from 'lucide-react'

interface Payout {
  id: string;
  period_label: string;
  amount: number;
  paid_at: string;
  status: string;
}

export function Billing() {
  const { data: billingData, isLoading } = useQuery({
    queryKey: ['billing-overview'],
    queryFn: api.getBillingOverview,
  })

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Partner Billing</h1>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              This Month's Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${billingData?.current_month_revenue || 0}</p>
            <p className="text-sm text-muted-foreground">From {billingData?.active_clients_count || 0} clients</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Total Lifetime Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${billingData?.total_revenue_earned || 0}</p>
            <p className="text-sm text-muted-foreground">Since {billingData?.partner_since || 'N/A'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Next Payout
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${billingData?.next_payout_amount || 0}</p>
            <p className="text-sm text-muted-foreground">On {billingData?.next_payout_date || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payout History */}
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Paid On</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingData?.payout_history?.map((payout: Payout) => (
                <TableRow key={payout.id}>
                  <TableCell>{payout.period_label}</TableCell>
                  <TableCell>${payout.amount}</TableCell>
                  <TableCell>{payout.paid_at}</TableCell>
                  <TableCell>
                    <Badge variant={payout.status === 'completed' ? 'secondary' : 'outline'}>
                      {payout.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              )) || (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No payout history yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default Billing
